import React from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay';
import { connect } from 'react-redux';
import get from 'lodash.get';

// Components
import Note from './Note';
import SelectableContainer from './SelectableContainer';
/* eslint no-unused-vars:0 */
// generic name because relay fragment is named: Tile_item
import Tile from 'dibs-search-product-tile';
import { TagProjectCTA, TagProjectModalRenderer } from 'dibs-project-forms';
import { FormattedMessage } from 'react-intl';
import { ServerVarsContextConsumer } from '../../../sharedComponents/ServerVarsContext';

// Helpers
import actions from '../../actions/FavoritesActions';
import { deviceTypes, tabs, trackingLists } from '../../constants';
import classnames from 'classnames';

// Styles
import styles from './styles/Tile.scss';

// Tracking
import { trackProductClick } from '../../helpers/trackingHelpers';
import trackingHelpers from '../../helpers/trackingHelpers';

class ItemPreviews extends React.Component {
    constructor(props) {
        super(props);

        this.selectItem = this.selectItem.bind(this);
        this.handleProductClick = this.handleProductClick.bind(this);
        this.state = {
            isHovering: false,
            isModalOpen: false,
        };
    }

    selectItem(id, portItemId, imgSrc, selected, imgTitle) {
        if (!this.props.selecting) {
            this.props.setSelectMode(true);
        }
        if (selected) {
            const newSelectedItems = this.props.selectedItems.filter(
                item => item.portfolioItemId !== portItemId
            );
            if (this.props.selectedItems.length === 1) {
                this.props.setSelectMode(false);
            }
            this.props.selectItems(newSelectedItems);
            this.props.setSelectAll('');
        } else if (!selected) {
            if (this.props.totalItems === this.props.selectedItems.length + 1) {
                this.props.setSelectAll('select');
            }
            this.props.selectItems(
                this.props.selectedItems.concat([
                    {
                        itemId: id,
                        portfolioItemId: portItemId,
                        portfolioId: id,
                        src: imgSrc,
                        title: imgTitle,
                    },
                ])
            );
        }
    }

    handleProductClick(item, index, tab) {
        if (tab === tabs.ITEMS) {
            trackProductClick(item, trackingLists.ITEMS, index);
        } else {
            trackProductClick(item, trackingLists.FOLDER, index);
        }
    }

    render() {
        const {
            isPublic,
            item,
            index,
            note,
            portfolioItemId,
            relay,
            reordering,
            selecting,
            selectedItems,
            showDealerBranding,
            showProjects,
            tab,
            projects,
            user,
            viewer,
        } = this.props;
        const isVisuallySimilarEnabled = get(viewer, 'isVisuallySimilarEnabled');

        const { firstPhotoWebPath, serviceId, title } = item;

        const tagProjectSource =
            tab === tabs.ITEMS ? 'favorites-items-grid-view' : 'favorites-folder-grid-view';

        const closeModal = () => {
            this.setState({ isModalOpen: false });
        };

        const selected = selectedItems.find(
            selectedItem =>
                selectedItem.itemId === serviceId &&
                selectedItem.portfolioItemId === portfolioItemId
        );
        const isTablet = this.props.deviceType === deviceTypes.TABLET;
        const tileClasses = classnames(styles.spacers, this.props.tileClasses, {
            [styles.hasProjects]: showProjects,
        });
        return (
            <div
                className={`colXs12 colMd6 colLg4 colXl3 ${styles.container}`}
                data-tn={`item-outer-container-${index}`}
                onClick={() =>
                    selecting
                        ? this.selectItem(
                              serviceId,
                              portfolioItemId,
                              firstPhotoWebPath,
                              selected,
                              title
                          )
                        : null
                }
                onMouseEnter={() => this.setState({ isHovering: true })}
                onMouseLeave={() => this.setState({ isHovering: false })}
            >
                <div className={tileClasses}>
                    {isPublic ? null : (
                        <SelectableContainer
                            isTablet={isTablet}
                            reordering={reordering}
                            selected={selected}
                            selectItem={() =>
                                this.selectItem(
                                    serviceId,
                                    portfolioItemId,
                                    firstPhotoWebPath,
                                    selected,
                                    title
                                )
                            }
                            selecting={selecting}
                            isHovering={this.state.isHovering}
                        />
                    )}
                    <div className={styles.tile} data-tn={`item-container-${index}`}>
                        <ServerVarsContextConsumer>
                            {({ isMobile }) => (
                                <Tile
                                    currency={this.props.currency}
                                    disableLinks={selecting}
                                    index={index}
                                    isMobile={isMobile}
                                    isVerifiedTrade={user ? user.isVerifiedTrade : false}
                                    item={item}
                                    itemsPerRow={4}
                                    showSellerName={showDealerBranding}
                                    onClick={() => this.handleProductClick(item, index + 1, tab)}
                                    showQuickView={false}
                                    useLoFiLazyLoader
                                />
                            )}
                        </ServerVarsContextConsumer>
                    </div>
                    {showProjects && (
                        <TagProjectCTA
                            className={styles.projectCTA}
                            canAddProject={!isPublic}
                            disabled={selecting}
                            projects={projects}
                            onClick={event => {
                                event.preventDefault();
                                this.setState({ isModalOpen: true });
                            }}
                            displayInSingleRow
                        />
                    )}
                    <Note
                        disabled={selecting}
                        hasExistingNote={note ? true : false}
                        noteContent={note}
                        onClick={event => {
                            event.preventDefault();
                            this.props.addNote({
                                ...item,
                                note,
                                portfolioItemId,
                            });
                        }}
                        isPublic={isPublic}
                    />
                    {isVisuallySimilarEnabled && (
                        <div className={styles.visuallySimilarWrapper}>
                            <a
                                className={styles.link}
                                href={`/similar/?item=${get(item, 'serviceId')}`}
                            >
                                <FormattedMessage
                                    id="portfolio.itemPreviews.browseSimilarPieces"
                                    defaultMessage="Browse Similar Pieces"
                                />
                            </a>
                        </div>
                    )}
                </div>
                {this.state.isModalOpen && (
                    <TagProjectModalRenderer
                        environment={relay.environment}
                        isOpen
                        itemId={get(item, 'serviceId')}
                        onClose={closeModal}
                        onDone={closeModal}
                        source={tagProjectSource}
                        userId={get(user, 'serviceId')}
                    />
                )}
            </div>
        );
    }
}

ItemPreviews.propTypes = {
    addNote: PropTypes.func,
    currency: PropTypes.string,
    deviceType: PropTypes.string,
    tileClasses: PropTypes.string,
    index: PropTypes.number,
    isPublic: PropTypes.bool,
    isTablet: PropTypes.bool,
    item: PropTypes.object,
    note: PropTypes.string,
    projects: PropTypes.array,
    portfolioItemId: PropTypes.string,
    relay: PropTypes.object,
    reordering: PropTypes.bool,
    selecting: PropTypes.bool,
    selectedItems: PropTypes.array,
    selectItems: PropTypes.func,
    setSelectAll: PropTypes.func,
    setSelectMode: PropTypes.func,
    showDealerBranding: PropTypes.bool,
    showProjects: PropTypes.bool,
    tab: PropTypes.string,
    totalItems: PropTypes.number,
    user: PropTypes.object,
    viewer: PropTypes.object,
};

const mapStateToProps = state => {
    return {
        deviceType: state.favorites.uiState.deviceType,
        reordering: state.favorites.uiState.reordering,
        selectAllStatus: state.favorites.uiState.selectAllStatus,
        selectedItems: state.favorites.uiState.selectedItems,
        selecting: state.favorites.uiState.selecting,
        tab: state.favorites.uiState.tab,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        selectItems: selectedItems => {
            dispatch(actions.selectItems({ selectedItems }));
        },
        setSelectAll: selectAllStatus => {
            dispatch(actions.setSelectAll({ selectAllStatus }));
        },
        setSelectMode: selecting => {
            dispatch(actions.setSelectMode({ selecting }));
        },
    };
};

const ReduxWrapped = connect(
    mapStateToProps,
    mapDispatchToProps
)(ItemPreviews);
ItemPreviews = createFragmentContainer(ReduxWrapped, {
    viewer: graphql`
        fragment ItemPreviews_viewer on Viewer {
            isVisuallySimilarEnabled: isEnabledByLocale(featureName: "VISUALLY_SIMILAR")
        }
    `,
    item: graphql`
        fragment ItemPreviews_item on Item {
            serviceId
            title
            ...trackingHelpers_item @relay(mask: false)
            firstPhotoWebPath(size: large)
            ...Tile_item @arguments(showSeller: $showSeller, isTrade: $isTrade)
        }
    `,
});

export default ItemPreviews;
